<script setup lang="ts">
import { ref } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import type { Props } from './Header.types';
import CartHeader from './CartHeader.vue';
import SearchInput from './SearchInput.vue';
import BackgroundMask from '@shop/components/ui/BackgroundMask.vue';
import useMobileMenuStore from '@shop/store/mobileMenu';

const props = withDefaults(defineProps<Props>(), {
  headerBgColor: '#6e6e6e',
  showMobileMenu: false,
  noCart: false,
  includeSearch: true,
  mobileMenuLabel: 'Menu',
  wishlistUri: '',
  cartUri: '',
});

const mobileMenuStore = useMobileMenuStore();
const isUserScrolling = ref(false);
const headerPositionRef = ref<HTMLElement | null>(null);

useIntersectionObserver(
  headerPositionRef,
  ([entry]) => {
    isUserScrolling.value = !entry.isIntersecting;
  },
  {
    threshold: 1,
    rootMargin: '0px',
  }
);
</script>

<template>
  <div ref="headerPositionRef" class="header__position"></div>
  <div class="header__container" :style="{ '--header-bg-color': props.headerBgColor }">
    <div class="header__wrapper">
      <div class="header__container_top">
        <div class="header__top">
          <slot name="header-top" />
        </div>
      </div>
      <div class="header__container_main">
        <div class="header__main">
          <div
            class="header__mobile_menu"
            :class="{ 'header__mobile_menu--active': mobileMenuStore.isOpen }"
            @click="mobileMenuStore.toggle()"
          >
            <span class="header__burger"></span>
            <span class="header__burger"></span>
            <span class="header__burger"></span>
            <span class="header__burgertext">{{ props.mobileMenuLabel }}</span>
          </div>
          <div class="header__logo-container">
            <slot name="header-logo" />
          </div>
          <SearchInput v-if="includeSearch" v-cloak />
          <div v-if="!props.noCart" class="header__cart-container">
            <CartHeader :wishlist-uri="props.wishlistUri" :cart-uri="props.cartUri" />
          </div>
        </div>
        <transition name="slide">
          <div v-show="mobileMenuStore.isOpen" v-cloak class="main-menu__container-mobile">
            <slot name="header-mobile-menu" />
          </div>
        </transition>
      </div>
      <!--      <div class="header__flashmessages" is="vue-flash-messages"></div>-->
      <div class="header__divider" :class="{ 'header__divider--sticky': isUserScrolling }"></div>
    </div>
  </div>
  <BackgroundMask />
</template>

<style lang="scss">
@use '@shop/assets/scss/abstracts/variables' as *;
@use '@shop/assets/scss/abstracts/mixins' as *;

.header {
  &__top,
  &__main,
  &__menu,
  &__flashmessages {
    z-index: 1;
    position: relative;
    width: 100%;

    @include tablet-portrait {
      box-shadow: none;
      width: $width-tablet-portrait;
      margin: 0 auto;
    }

    @include tablet-landscape {
      box-shadow: none;
      width: $width-tablet-landscape;
      margin: 0 auto;
    }

    @include desktop {
      box-shadow: none;
      width: $width-desktop;
      margin: 0 auto;
    }
  }

  &__position {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 1px;
    pointer-events: none;
    z-index: -1;
  }

  &__divider {
    position: sticky;
    top: 0;
    height: 0;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;

    &--sticky {
      opacity: 1;
      background:
        linear-gradient(to right, $white, $white),
        linear-gradient(0deg, #ffffff00, $bordergray 108%);
      border-bottom: $width-box-border-width solid transparent;
      background-clip: padding-box, border-box;
      background-origin: padding-box, border-box;

      & + .flashmessage__container {
        margin-top: 0;
      }
    }
  }

  &__container {
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 801;
  }

  &__wrapper {
    position: fixed;
    width: 100%;
    top: 0;
    max-height: 100%;

    @include mobile-up {
      overflow-y: inherit;
    }
  }

  &__container_top {
    background: var(--header-bg-color);
  }

  &__container_main {
    position: relative;
    background: $white;
  }

  &__top {
    display: none;

    @include mobile-up {
      display: block;
      color: $white;
      height: 37px;
      line-height: 37px;
    }

    .anchor {
      text-decoration: none !important;
    }
  }

  &__main {
    background: $white;
    position: relative;

    @include mobile-up {
      min-height: 88px;
    }
  }

  &__cart-container {
    position: absolute;
    top: 15px;
    right: $width-mobile-gutter;

    @include mobile-up {
      top: 20px;
      right: 0;
    }
  }

  &__logo-container {
    width: 65%;
    text-align: left;
    display: inline-block;

    @include mobile-up {
      text-align: inherit;
      width: auto;
    }

    a {
      box-shadow: none !important;
    }
  }

  &__mobile_menu {
    display: inline-block;
    width: 55px;
    height: 60px;
    vertical-align: top;
    padding: 15px;
    cursor: pointer;

    @include mobile-up {
      display: none;
    }
  }

  &__burger {
    background: var(--store-color);
    display: block;
    width: 25px;
    height: 4px;
    margin-bottom: 5px;

    &:nth-of-type(1) {
      .header__mobile_menu--active & {
        transform: translateY(9px) rotate(45deg);
      }
    }

    &:nth-of-type(2) {
      .header__mobile_menu--active & {
        opacity: 0;
      }
    }

    &:nth-of-type(3) {
      margin-bottom: 3px;

      .header__mobile_menu--active & {
        transform: translateY(-9px) rotate(-45deg);
      }
    }
  }

  &__burgertext {
    display: block;
    font-size: 0.5em;
    width: 25px;
    text-align: center;
  }

  &__sellingpoint-list {
    display: inline-block;
    margin: 0;
    padding: 0;
  }

  &__sellingpoint-item {
    display: inline-block;
    padding-right: 34px;

    @include tablet-portrait {
      padding-right: 20px;
    }

    &:before {
      margin-right: 10px;
    }
  }

  &__customermenu {
    display: flex;
    gap: 4px;
    padding: 0;
    margin: 0;
    float: right;
  }

  &__customermenu-item {
    display: inline-block;
    text-decoration: none;
    color: $white;

    &:not(:last-child) {
      &:after {
        content: '|';
      }
    }
  }

  &__container_menu {
    display: none;

    @include mobile-up {
      border-top: 1px solid $bordergray;
      display: block;
      height: 40px;
      background: $boxgray;
      width: 100%;
      margin-top: 124px;
    }
  }

  &__menu {
    box-sizing: border-box;
    display: none;
    height: 40px;
    padding: 0;
    list-style-type: none;
    line-height: 40px;

    @include mobile-up {
      display: block;
    }
  }

  &__menu_item {
    box-sizing: border-box;
    display: inline-block;
    height: 40px;
    width: auto;
    -webkit-box-flex: 1;

    &--active {
      border-bottom: 3px solid var(--store-color);
      width: auto;
    }
  }

  &__logo {
    margin: 14px 0 0 0;

    @include mobile-up {
      margin: 19px 0 0 0;
    }
  }

  &__search-container {
    position: absolute;
  }
}
</style>
